import { z } from 'zod'

// 과거에 1년간 저장하던 로그인 국가 쿠키. 신규 로그인 분기에서는 더 이상 읽거나 저장하지 않고,
// 사용자가 국가를 다시 선택할 때 제거한다. i18n은 기존 사용자 마이그레이션 기간 동안 fallback으로만 읽는다.
export const LOGIN_COUNTRY_COOKIE_KEY = 'PODO_LOGIN_COUNTRY'

export const loginCountrySchema = z.enum(['KR', 'JP'])
export type LoginCountry = z.infer<typeof loginCountrySchema>

export const parseLoginCountry = (value: unknown): LoginCountry | null => {
  const parsed = loginCountrySchema.safeParse(value)
  return parsed.success ? parsed.data : null
}

/**
 * JP 전용 퍼널(일본 체험레슨 신청·플랜 결제)에서 로그인으로 진입한 destination.
 * 이 진입은 IP 국가가 KR 이거나 판별 불가여도 국가 선택을 노출해
 * KR(카카오)/JP(라인) 로그인 분기를 사용자가 직접 결정하게 한다.
 *
 * TRIAL_APPLICATION 은 이름과 달리 KR 체험 퍼널이 아니라 JP 전용이다
 * (KR 은 TRIAL_TICKET_LIST → /subscribes/trial, JP 는 TRIAL_APPLICATION → /subscribes/jp/trial).
 */
const JP_FUNNEL_DESTINATIONS: readonly string[] = ['JP_CHECKOUT', 'JP_PAYMENT_RETURN', 'TRIAL_APPLICATION']

export const isJpFunnelDestination = (destination: string | undefined): boolean =>
  destination !== undefined && JP_FUNNEL_DESTINATIONS.includes(destination)

type LoginCountryGateParams = {
  refreshToken?: string | null
  selectedCountry: LoginCountry | null
  requestIpRegion: LoginIpRegion | null
  forceCountrySelection?: boolean
}

type CountrySelectionVisibilityParams = Pick<LoginCountryGateParams, 'requestIpRegion' | 'forceCountrySelection'>

export type LoginIpRegion = 'KR' | 'FOREIGN'

export const shouldShowCountrySelection = ({
  requestIpRegion,
  forceCountrySelection = false,
}: CountrySelectionVisibilityParams): boolean => {
  // 한국 IP만 기존 KR 로그인으로 바로 진입한다. 해외이거나 IP를 판별하지 못하면
  // KR로 추정하지 않고 사용자가 로그인 국가를 직접 선택하게 한다.
  return forceCountrySelection || requestIpRegion !== 'KR'
}

export const shouldRequestLoginCountry = ({
  refreshToken,
  selectedCountry,
  requestIpRegion,
  forceCountrySelection,
}: LoginCountryGateParams): boolean => {
  // IP는 로그인 전 국가 선택에만 사용한다. 로그인 세션이 있으면 검증을 먼저 거쳐
  // 이후 화면에서 GT_USER.COUNTRY_CODE를 정본으로 사용한다.
  if (refreshToken || selectedCountry !== null) {
    return false
  }

  return shouldShowCountrySelection({ requestIpRegion, forceCountrySelection })
}

const LOGIN_IP_REGION_HEADER = 'x-podo-login-ip-region'
const IP_COUNTRY_HEADERS = [
  'x-podo-client-country',
  'x-client-region',
  'x-appengine-country',
  'x-vercel-ip-country',
  'cf-ipcountry',
  'cloudfront-viewer-country',
] as const

const normalizeLoginIpRegion = (value: string): LoginIpRegion | null => {
  const region = value.trim().toUpperCase()
  return region === 'KR' || region === 'FOREIGN' ? region : null
}

const normalizeIpCountryCode = (value: string | null): string | null => {
  if (!value) {
    return null
  }

  const countryCode = value.split(',', 1)[0]?.trim().toUpperCase()
  return countryCode && /^[A-Z]{2}$/.test(countryCode) && countryCode !== 'ZZ' ? countryCode : null
}

/**
 * Cloud Armor가 클라이언트 값을 덮어쓰는 전용 헤더를 가장 먼저 사용한다.
 * UNKNOWN/잘못된 값이 있으면 다른 클라이언트 헤더로 폴백하지 않고 판별 불가로 처리한다.
 * 전용 헤더가 없는 플랫폼에서는 기존 IP 국가 헤더를 KR/해외로만 축약한다.
 */
export const resolveLoginIpRegion = (requestHeaders: Pick<Headers, 'get'>): LoginIpRegion | null => {
  const trustedRegion = requestHeaders.get(LOGIN_IP_REGION_HEADER)
  if (trustedRegion !== null) {
    return normalizeLoginIpRegion(trustedRegion)
  }

  for (const headerName of IP_COUNTRY_HEADERS) {
    const headerValue = requestHeaders.get(headerName)
    if (headerValue !== null) {
      const countryCode = normalizeIpCountryCode(headerValue)
      return countryCode === null ? null : countryCode === 'KR' ? 'KR' : 'FOREIGN'
    }
  }

  return null
}
